import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import {
  PointLeaderboardService,
  User,
  RankingsResponse,
  Season,
  TimelineResponse,
  CLUBS_PAGE_SIZE,
  GetUsersParams,
} from '../../../utilities/services/point-leaderboard/point-leaderboard.service';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  switchMap,
} from 'rxjs/operators';
import { Subject, of } from 'rxjs';
import { SkeletonLoaderComponent } from '../../shared/skeleton-loader/skeleton-loader.component';
// Define custom Event interface to avoid conflict with DOM Event
interface CustomEvent {
  event_id: number;
  event_name: string;
}

@Component({
  selector: 'stupa-leaderboard-sidebar',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, SkeletonLoaderComponent],
  templateUrl: './leaderboardsidebar.component.html',
  styleUrl: './leaderboardsidebar.component.scss',
})
export class LeaderboardSidebarComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @ViewChild('clubOptionsList') clubOptionsList?: ElementRef<HTMLElement>;
  @Output() panelClose = new EventEmitter<void>();
  @Output() applyFilters = new EventEmitter<{
    parents: number[];
    filterRankMin?: number;
    filterRankMax?: number;
    dateFrom?: string;
    dateTo?: string;
    filterEventIds?: number[];
    filterSeasonId?: string;
  }>();

  @Input() parents: number[] = [];
  @Input() clubs: User[] = [];
  @Input() clubsRoleId: number | null = null;
  @Input() seasons: Season[] = [];
  @Input() events: CustomEvent[] = [];
  @Input() timeline: TimelineResponse | null = null;
  @Input() selectedSeasonId: string = '';
  @Input() selectedTimelineId: string | null = null;
  @Input() timelineSeasonId: string = '';
  @Input() filterRankMin?: number;
  @Input() filterRankMax?: number;
  @Input() dateFrom?: string;
  @Input() dateTo?: string;
  @Input() filterEventIds: number[] = [];
  @Input() totalItems: number = 0; // Total number of standings
  @Input() filterSeasonId: string = ''; // Season ID from applied filters
  @Input() parent_role_list: any[] = []; // From parent (point-leaderboard) - no duplicate API call

  activeTab: string = 'club'; // 'club' for role tabs, or 'period', 'rank', 'event'
  activeRoleId: number | null = null; // Selected parent_role_id when in role tab
  readonly filterTitleKeyByRole: Readonly<Record<string, string>> = {
    'Member Association': 'leaderboard_filter_by_member_association',
    'Club': 'leaderboard_filter_by_club',
  };
  rankOptions: string[] = [
    'Top 20',
    '20-50',
    '50-100',
    '100-200',
    '200-300',
    '300-400',
    '400-500',
    '500-600',
    '600-700',
    '700-800',
    '800-900',
    '900-1000',
    '1000 & Up',
  ];

  filteredRankOptions: string[] = [];
  selectedRank: string | null = null; // No default selection
  selectedClubs: { [key: number]: boolean } = {};
  searchValue: string = '';
  private clubsPageNum = 1;
  clubsLoading = false;
  clubsHasMore = true;
  private clubsTotalCount = 0;
  private clubOptionsScrollCleanup: (() => void) | null = null;
  selectedEvents: { [key: number]: boolean } = {};
  eventSearchValue: string = '';
  eventTabSeasonId: string = ''; // New property for the event tab's season dropdown

  private readonly searchSubject = new Subject<string>();
  private readonly eventSearchSubject = new Subject<string>();
  private readonly categoryId: number;
  private _previousScrollY: number | null = null;
  private _scrollLockStyles: {
    overflow: string;
    overflowX: string;
    overflowY: string;
    position: string;
    top: string;
    width: string;
  } | null = null;

  constructor(
    private readonly pointLeaderboardService: PointLeaderboardService,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.categoryId =
      Number(this.route.snapshot.paramMap.get('categoryId')) || 10;
  }

  // New function to get the current date
  private getToday(): Date {
    return new Date();
  }

  ngOnInit() {
    this.lockBodyScroll();
    this.initFromParentRoleList();
    // Initialize selectedClubs from applied filters (parents) - only use clubs when no parent_role_list
    if (this.parent_role_list?.length === 0) {
      this.selectedClubs = {};
      this.clubs.forEach((club) => {
        this.selectedClubs[club.user_role_id] = this.parents.includes(
          club.user_role_id,
        );
      });
    }

    // Initialize selectedEvents based on filterEventIds
    this.events.forEach((event) => {
      this.selectedEvents[event.event_id] = this.filterEventIds.includes(event.event_id);
    });

    // Initialize selectedRank based on filterRankMin and filterRankMax
    this.initializeSelectedRank();

    // Initialize selectedTimelineId based on dateFrom and dateTo
    this.initializeSelectedTimeline();

    // Initialize selectedSeasonId: prefer filterSeasonId (applied filters), else selectedSeasonId from parent
    // Convert to string for consistent dropdown matching (API may return number)
    const initialSeason = this.filterSeasonId || this.selectedSeasonId || '';
    this.selectedSeasonId = initialSeason === '' ? '' : String(initialSeason);

    // Synchronize eventTabSeasonId with selectedSeasonId
    this.eventTabSeasonId = this.selectedSeasonId;

    // Filter rankOptions based on totalItems
    this.filterRankOptions();

    // Fetch all seasons for the dropdowns
    this.fetchSeasons();

    if (this.parent_role_list?.length === 0) {
      if (this.clubs.length > 0) {
        this.clubsPageNum = 1;
        this.clubsHasMore = this.clubs.length >= CLUBS_PAGE_SIZE;
      } else {
        this.fetchClubsPage(1, false);
      }
    }

    // Setup search subscriptions
    this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((searchValue) => {
          this.searchValue = searchValue;
          this.resetClubsList();
          const params = this.buildClubsUsersParams(1, searchValue);
          if (!params) {
            return of({ users: [], count: 0 });
          }
          return this.pointLeaderboardService.getUsers(params);
        }),
      )
      .subscribe(({ users, count }) => {
        this.applyClubsPage(users, false, count);
      });

    this.eventSearchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((searchValue) =>
          this.pointLeaderboardService.getRankings(
            Number(this.eventTabSeasonId),
            this.categoryId,
            1,
            100,
            [],
            undefined,
            undefined,
            undefined,
            undefined,
            '',
            'rank',
            'asc',
            []
          )
        )
      )
      .subscribe((response) => {
        this.updateEvents(response, this.eventSearchValue);
        this.filterRankOptions();
        this.cdr.detectChanges();
      });

    setTimeout(() => this.attachClubOptionsScrollListener(), 0);
  }

  ngAfterViewInit(): void {
    this.attachClubOptionsScrollListener();
  }

  ngOnDestroy() {
    this.detachClubOptionsScrollListener();
    this.unlockBodyScroll();
  }

  private lockBodyScroll(): void {
    const html = document.documentElement;
    const body = document.body;
    this._previousScrollY = globalThis.window.scrollY ?? globalThis.window.pageYOffset;
    this._scrollLockStyles = {
      overflow: body.style.overflow,
      overflowX: body.style.overflowX,
      overflowY: body.style.overflowY,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };
    body.style.overflow = 'hidden';
    body.style.overflowX = 'hidden';
    body.style.overflowY = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${this._previousScrollY}px`;
    body.style.width = '100%';
    html.style.overflow = 'hidden';
  }

  private unlockBodyScroll(): void {
    const body = document.body;
    const html = document.documentElement;
    if (this._scrollLockStyles) {
      body.style.overflow = this._scrollLockStyles.overflow;
      body.style.overflowX = this._scrollLockStyles.overflowX;
      body.style.overflowY = this._scrollLockStyles.overflowY;
      body.style.position = this._scrollLockStyles.position;
      body.style.top = this._scrollLockStyles.top;
      body.style.width = this._scrollLockStyles.width;
      this._scrollLockStyles = null;
    }
    html.style.overflow = '';
    if (typeof this._previousScrollY === 'number') {
      globalThis.window.scrollTo(0, this._previousScrollY);
      this._previousScrollY = null;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['totalItems']) {
      this.filterRankOptions();
    }
    if (changes['parents']) {
      this.syncSelectedClubsFromParents();
    }
    if (changes['filterEventIds']) {
      this.syncSelectedEventsFromFilter();
    }
    if (changes['filterRankMin'] || changes['filterRankMax']) {
      this.initializeSelectedRank();
    }
    if (changes['dateFrom'] || changes['dateTo']) {
      this.initializeSelectedTimeline();
    }
    if (changes['selectedSeasonId']) {
      this.applySeasonSelection(changes['selectedSeasonId'].currentValue);
    }
    if (changes['filterSeasonId']) {
      this.applySeasonSelection(changes['filterSeasonId'].currentValue);
    }
    if (changes['parent_role_list']) {
      this.initFromParentRoleList();
    }
  }

  private syncSelectedClubsFromParents(): void {
    this.selectedClubs = {};
    if (this.parent_role_list?.length > 0) {
      this.initializeSelectedFromParents();
      return;
    }
    this.clubs.forEach((club) => {
      this.selectedClubs[club.user_role_id] = this.parents.includes(
        club.user_role_id,
      );
    });
  }

  private syncSelectedEventsFromFilter(): void {
    this.events.forEach((event) => {
      this.selectedEvents[event.event_id] = this.filterEventIds.includes(
        event.event_id,
      );
    });
  }

  private applySeasonSelection(value: unknown): void {
    if (value == null || value === '') {
      this.selectedSeasonId = '';
    } else if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      this.selectedSeasonId = String(value);
    } else {
      this.selectedSeasonId = '';
    }
    this.eventTabSeasonId = this.selectedSeasonId;
    this.cdr.detectChanges();
  }

  private initFromParentRoleList(): void {
    if (this.parent_role_list?.length > 0) {
      this.activeTab = 'club';
      this.activeRoleId = this.parent_role_list[0].parent_role_id;
      this.initializeSelectedFromParents();
      this.resetClubsList();
      this.fetchClubsPage(1, false);
    }
    this.cdr.detectChanges();
    setTimeout(() => this.attachClubOptionsScrollListener(), 0);
  }

  private filterRankOptions() {
    // Always show all rank options (no filtering by totalItems)
    this.filteredRankOptions = [...this.rankOptions];
    this.cdr.detectChanges();
  }

  private getRankRange(option: string): { min: number; max: number } {
    if (option === 'Top 20') {
      return { min: 1, max: 20 };
    } else if (option === '1000 & Up') {
      return { min: 1001, max: Number.MAX_SAFE_INTEGER };
    }
    const [min, max] = option.split('-').map(Number);
    return { min: min + 1, max };
  }

  private initializeSelectedRank() {
    if (this.filterRankMin !== undefined && this.filterRankMax !== undefined) {
      if (this.filterRankMin === 1 && this.filterRankMax === 20) {
        this.selectedRank = 'Top 20';
      } else if (this.filterRankMin === 1001) {
        this.selectedRank = '1000 & Up';
      } else {
        const matchingOption = this.rankOptions.find((option) => {
          const { min, max } = this.getRankRange(option);
          return min === this.filterRankMin && max === this.filterRankMax;
        });
        this.selectedRank = matchingOption || null;
      }
    } else {
      this.selectedRank = null;
    }
    this.cdr.detectChanges();
  }

  private initializeSelectedTimeline() {
    if (this.dateFrom && this.timeline) {
      const timelineArray = this.getTimelineArray(this.timeline);
      const matchingItem = timelineArray.find((item) => {
        if (this.timeline?.configure === 'month') {
          const date = new Date(item.date);
          const year = date.getFullYear();
          const month = date.getMonth();
          const lastDay = new Date(year, month + 1, 0).getDate();
          const dateTo = `${year}-${String(month + 1).padStart(2, '0')}-${String(
            lastDay
          ).padStart(2, '0')}`;
          return item.date === this.dateFrom && dateTo === this.dateTo;
        } else if (this.timeline?.configure === 'week') {
          const date = new Date(item.date);
          date.setDate(date.getDate() + 6);
          const dateTo = `${date.getFullYear()}-${String(
            date.getMonth() + 1
          ).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          return item.date === this.dateFrom && dateTo === this.dateTo;
        } else {
          return item.date === this.dateFrom;
        }
      });
      this.selectedTimelineId = matchingItem ? matchingItem.date : null;
    } else {
      this.selectedTimelineId = null;
    }
    this.cdr.detectChanges();
  }

  private getTimelineDateRange(): { dateFrom?: string; dateTo?: string } {
    if (!this.selectedTimelineId || !this.timeline) {
      
      return {};
    }
    const timelineArray = this.getTimelineArray(this.timeline);
    const selectedItem = timelineArray.find(
      (item) => item.date === this.selectedTimelineId
    );
    if (!selectedItem) {
      
      return {};
    }

    const dateFrom = selectedItem.date;
    let dateTo = dateFrom;

    if (this.timeline.configure === 'month') {
      const date = new Date(dateFrom);
      const year = date.getFullYear();
      const month = date.getMonth();
      const lastDay = new Date(year, month + 1, 0).getDate();
      dateTo = `${year}-${String(month + 1).padStart(2, '0')}-${String(
        lastDay
      ).padStart(2, '0')}`;
    } else if (this.timeline.configure === 'week') {
      const date = new Date(dateFrom);
      date.setDate(date.getDate() + 6); // +7 days, inclusive of start
      dateTo = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        '0'
      )}-${String(date.getDate()).padStart(2, '0')}`;
    }

    
    return { dateFrom, dateTo };
  }

  setActiveTab(tab: string) {
    this.activeTab = tab;
    if (tab === 'club' && this.parent_role_list?.length > 0 && !this.activeRoleId) {
      this.activeRoleId = this.parent_role_list[0].parent_role_id;
      this.resetClubsList();
      this.fetchClubsPage(1, false);
    }
    // Fetch timeline when switching to period tab if season is selected
    if (tab === 'period' && this.selectedSeasonId) {
      this.fetchTimeline();
    }
    this.cdr.detectChanges();
  }

  onRankSelect(option: string | null) {
    this.selectedRank = option;
    this.cdr.detectChanges();
  }

  onClubSelect(userRoleId: number) {
    this.selectedClubs[userRoleId] = !this.selectedClubs[userRoleId];
    this.cdr.detectChanges();
  }

  isRoleEntitySelected(parentUserRoleId: number): boolean {
    return !!this.selectedClubs[parentUserRoleId];
  }

  onEventSelect(eventId: number) {
    this.selectedEvents[eventId] = !this.selectedEvents[eventId];
    this.cdr.detectChanges();
  }

  onSeasonSelect(seasonId: string) {
    this.selectedSeasonId = seasonId;
    // Synchronize eventTabSeasonId with selectedSeasonId
    this.eventTabSeasonId = seasonId;
    // Clear events when season changes (will be fetched on Apply)
    this.events = [];
    this.selectedEvents = {};
    // Fetch timeline when season is selected (but not getRankings API)
    if (seasonId) {
      this.fetchTimeline();
    } else {
      // Clear timeline when "None" is selected
      this.timeline = null;
      this.selectedTimelineId = null;
    }
    this.cdr.detectChanges();
  }

  onEventSeasonSelect(seasonId: string) {
    this.eventTabSeasonId = seasonId;
    // Synchronize selectedSeasonId with eventTabSeasonId
    this.selectedSeasonId = seasonId;
    this.eventSearchValue = ''; // Clear search when season changes
    this.events = []; // Clear events list (will be fetched on Apply)
    this.selectedEvents = {}; // Clear selected events
    // Fetch timeline when season is selected (but not getRankings API)
    if (seasonId) {
      this.fetchTimeline();
    } else {
      // Clear timeline when "None" is selected
      this.timeline = null;
      this.selectedTimelineId = null;
    }
  }

  onTimelineSelect(timelineId: string | null) {
    
    this.selectedTimelineId = timelineId;
    // Force immediate change detection to ensure selectedTimelineId is updated
    this.cdr.detectChanges();
    // Compute date range immediately to ensure it's available
    this.getTimelineDateRange();
    
    this.cdr.detectChanges();
  }

  onSearch() {
    if (this.activeTab === 'club') {
      this.searchSubject.next(this.searchValue);
    }
  }

  onClubOptionsScroll(event: Event): void {
    const el = event.currentTarget as HTMLElement;
    this.tryLoadMoreClubsFromScroll(el);
  }

  loadMoreClubs(): void {
    if (this.clubsLoading || !this.clubsHasMore) {
      return;
    }
    this.fetchClubsPage(this.clubsPageNum + 1, true);
  }

  private getClubsRoleId(): number | null {
    if (this.parent_role_list?.length > 0 && this.activeRoleId != null) {
      return this.activeRoleId;
    }
    return this.clubsRoleId;
  }

  private buildClubsUsersParams(
    pageNum: number,
    searchValue?: string,
  ): GetUsersParams | null {
    const roleId = this.getClubsRoleId();
    if (roleId == null) {
      return null;
    }
    return {
      roleId,
      searchValue: searchValue ?? this.searchValue,
      pageNum,
      perPage: CLUBS_PAGE_SIZE,
      skipGlobalLoader: true,
    };
  }

  private resetClubsList(): void {
    this.clubs = [];
    this.clubsPageNum = 1;
    this.clubsHasMore = true;
    this.clubsTotalCount = 0;
  }

  private fetchClubsPage(page: number, append: boolean): void {
    const roleId = this.getClubsRoleId();
    if (!roleId || this.clubsLoading) {
      return;
    }
    if (page > 1 && !this.clubsHasMore) {
      return;
    }

    this.clubsLoading = true;
    const params = this.buildClubsUsersParams(page);
    if (!params) {
      this.clubsLoading = false;
      return;
    }
    this.pointLeaderboardService
      .getUsers(params)
      .pipe(
        finalize(() => {
          this.clubsLoading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe(({ users, count }) => {
        this.clubsPageNum = page;
        this.applyClubsPage(users, append, count);
      });
  }

  private tryLoadMoreClubsFromScroll(el: HTMLElement): void {
    if (!el || this.clubsLoading || !this.clubsHasMore) {
      return;
    }
    const threshold = 80;
    const nearBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    if (nearBottom) {
      this.loadMoreClubs();
    }
  }

  private attachClubOptionsScrollListener(): void {
    this.detachClubOptionsScrollListener();
    const el = this.clubOptionsList?.nativeElement;
    if (!el || this.activeTab !== 'club') {
      return;
    }
    const handler = () => this.tryLoadMoreClubsFromScroll(el);
    el.addEventListener('scroll', handler, { passive: true });
    this.clubOptionsScrollCleanup = () =>
      el.removeEventListener('scroll', handler);
  }

  private detachClubOptionsScrollListener(): void {
    if (this.clubOptionsScrollCleanup) {
      this.clubOptionsScrollCleanup();
      this.clubOptionsScrollCleanup = null;
    }
  }

  private applyClubsPage(
    users: User[],
    append: boolean,
    totalCount?: number,
  ): void {
    this.clubs = append ? [...this.clubs, ...users] : users;
    if (totalCount != null && totalCount > 0) {
      this.clubsTotalCount = totalCount;
      this.clubsHasMore = this.clubs.length < totalCount;
    } else {
      this.clubsHasMore = users.length >= CLUBS_PAGE_SIZE;
    }
    const newSelectedClubs = { ...this.selectedClubs };
    users.forEach((club) => {
      if (!(club.user_role_id in newSelectedClubs)) {
        newSelectedClubs[club.user_role_id] = this.parents.includes(
          club.user_role_id,
        );
      }
    });
    this.selectedClubs = newSelectedClubs;
    this.filterRankOptions();
    this.cdr.detectChanges();
    setTimeout(() => this.attachClubOptionsScrollListener(), 0);
  }

  onEventSearch() {
    this.eventSearchSubject.next(this.eventSearchValue);
  }

  closeSidebar() {
    this.panelClose.emit();
    this.cdr.detectChanges();
  }

  applyFiltersHandler() {
    const parents = Object.keys(this.selectedClubs)
      .filter((key) => this.selectedClubs[Number(key)])
      .map(Number);
    const filterEventIds = Object.keys(this.selectedEvents)
      .filter((key) => this.selectedEvents[Number(key)])
      .map(Number);
    const filter = {
      parents,
      filterEventIds,
      filterSeasonId: this.selectedSeasonId || '', // Use selectedSeasonId and default to empty string if "None"
    } as {
      parents: number[];
      filterRankMin?: number;
      filterRankMax?: number;
      dateFrom?: string;
      dateTo?: string;
      filterEventIds?: number[];
      filterSeasonId?: string;
    };
    if (this.selectedRank) {
      const { min: filterRankMin, max: filterRankMax } = this.getRankRange(
        this.selectedRank
      );
      filter.filterRankMin = filterRankMin;
      filter.filterRankMax = filterRankMax;
    }
    if (this.selectedTimelineId) {
      const { dateFrom, dateTo } = this.getTimelineDateRange();
      if (dateFrom && dateTo) {
        filter.dateFrom = dateFrom;
        filter.dateTo = dateTo;
      }
    }
    
    this.applyFilters.emit(filter);
    this.closeSidebar();
  }

  private formatDateForApi(date: Date): string {
    // Format date as YYYY-MM-DD
    return date.toISOString().split('T')[0];
  }

  clearFilters() {
    this.selectedClubs = {};
    if (this.parent_role_list?.length > 0) {
      this.parent_role_list.forEach((role: any) => {
        (role.parent_user_roles || []).forEach((item: any) => {
          this.selectedClubs[item.parent_user_role_id] = false;
        });
      });
    } else {
      this.clubs.forEach((club) => {
        this.selectedClubs[club.user_role_id] = false;
      });
    }
    this.selectedRank = null;
    this.selectedEvents = {};
    this.events = []; // Clear events list
    this.selectedTimelineId = null;
    this.timeline = null; // Clear timeline when clearing filters
    this.searchValue = '';
    this.eventSearchValue = '';
    // Reset season selection to "None" (empty string)
    this.selectedSeasonId = '';
    this.eventTabSeasonId = '';

    this.applyFilters.emit({
      parents: [],
      filterRankMin: undefined,
      filterRankMax: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      filterEventIds: [],
      filterSeasonId: '', // Reset to "None" (empty string)
    });
    this.cdr.detectChanges();
    this.closeSidebar();
    // Fetch ongoing season to reset seasonId
    // const today = this.getToday();
    // this.pointLeaderboardService
    //   .getOngoingSeason(this.formatDateForApi(today))
    //   .subscribe({
    //     next: (response) => {
    //       this.seasons = response.seasons.map((season) => {
    //         const startDate = new Date(season.start_date);
    //         const endDate = new Date(season.end_date);
    //         const isCurrent = startDate <= today && today <= endDate;
    //         return {
    //           ...season,
    //           season_name: isCurrent
    //             ? `${season.season_name} (Current)`
    //             : season.season_name,
    //         };
    //       });
    //       // Set selectedSeasonId to the ongoing season, or fall back to first season if none is ongoing
    //       const ongoingSeason = this.seasons.find(
    //         (season) => season.status === 'ongoing'
    //       );
    //       this.selectedSeasonId = ongoingSeason
    //         ? ongoingSeason.id
    //         : this.seasons[0]?.id || '';
    //       this.eventTabSeasonId = this.selectedSeasonId; // Reset event tab season
    //       // Save selectedSeasonId to local storage
    //       localStorage.setItem('seasonId', this.selectedSeasonId);
    //       this.applyFilters.emit({
    //         filterClubIds: [],
    //         filterRankMin: undefined,
    //         filterRankMax: undefined,
    //         dateFrom: undefined,
    //         dateTo: undefined,
    //         filterEventIds: [],
    //         filterSeasonId: this.selectedSeasonId, // Reset to default season
    //       });
    //       this.cdr.detectChanges();
    //     },
    //     error: (error) => {
    //       console.error('Error fetching ongoing season in clearFilters:', error);
    //       this.selectedSeasonId = '';
    //       this.eventTabSeasonId = this.selectedSeasonId;
    //       localStorage.setItem('seasonId', this.selectedSeasonId);
    //       this.applyFilters.emit({
    //         filterClubIds: [],
    //         filterRankMin: undefined,
    //         filterRankMax: undefined,
    //         dateFrom: undefined,
    //         dateTo: undefined,
    //         filterEventIds: [],
    //         filterSeasonId: this.selectedSeasonId,
    //       });
    //       this.cdr.detectChanges();
    //     },
    //   });
  }

  public fetchClubs() {
    this.resetClubsList();
    this.fetchClubsPage(1, false);
  }

  public fetchEvents() {
    this.pointLeaderboardService
      .getRankings(
        Number(this.eventTabSeasonId),
        this.categoryId,
        1,
        100,
        [],
        undefined,
        undefined,
        undefined,
        undefined,
        '',
        'rank',
        'asc',
        []
      )
      .subscribe((response) => {
        this.updateEvents(response, this.eventSearchValue);
        // Reapply selectedEvents based on filterEventIds
        this.events.forEach((event) => {
          this.selectedEvents[event.event_id] = this.filterEventIds.includes(event.event_id);
        });
        this.filterRankOptions();
        this.cdr.detectChanges();
      });
  }

  public fetchSeasons() {
    this.pointLeaderboardService.getSeasons(1000).subscribe((response) => {
      const today = this.getToday();
      this.seasons = response.seasons.map((season) => {
        const startDate = new Date(season.start_date);
        const endDate = new Date(season.end_date);
        const isCurrent = startDate <= today && today <= endDate;
        return {
          ...season,
          season_name: isCurrent
            ? `${season.season_name} (Current)`
            : season.season_name,
        };
      });
      this.cdr.detectChanges();
    });
  }

  public fetchTimeline() {
    if (!this.selectedSeasonId) return;
    this.pointLeaderboardService
      .getRankingTimeline(Number(this.selectedSeasonId), this.categoryId)
      .subscribe((response) => {
        this.timeline = response;
        this.timelineSeasonId = this.selectedSeasonId; // Update timelineSeasonId
        this.initializeSelectedTimeline(); // Initialize after fetching timeline
        this.cdr.detectChanges();
        
      });
  }

  public getTimelineArray(response: TimelineResponse) {
    switch (response.configure) {
      case 'at_the_end_of_every_event':
      case 'day':
        return response.by_date;
      case 'month':
        return response.by_month;
      case 'week':
        return response.by_week;
      default:
        return [];
    }
  }

  private updateEvents(response: RankingsResponse, searchValue: string) {
    const eventMap: {
      [key: number]: {
        event_id: number;
        event_name: string;
        hasActivePoints: boolean;
      };
    } = {};

    response.standings.forEach((standing) => {
      standing.event_breakdown.forEach((event) => {
        const expiryDate = new Date(event.expiry_date);
        const isActive = expiryDate >= this.getToday();
        if (!eventMap[event.event_id]) {
          eventMap[event.event_id] = {
            event_id: event.event_id,
            event_name: event.event_name,
            hasActivePoints: isActive,
          };
        } else if (isActive) {
          eventMap[event.event_id].hasActivePoints = true;
        }
      });
    });

    let events = Object.values(eventMap)
      .filter((event) => event.hasActivePoints)
      .map((event) => ({
        event_id: event.event_id,
        event_name: event.event_name,
      }));

    if (searchValue) {
      events = events.filter((event) =>
        event.event_name.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    // Reset events if no valid events are found
    this.events = events.length > 0 ? events : [];
    this.selectedEvents = {}; // Clear selected events to prevent stale selections
    this.cdr.detectChanges();
  }
  getDisplayableRoles(): any[] {
    return this.parent_role_list || [];
  }

  /** Selected count for Club/parents filter */
  getSelectedClubCount(): number {
    return Object.keys(this.selectedClubs).filter((id) =>
      this.selectedClubs[Number(id)],
    ).length;
  }

  /** Selected count for a specific role's parent_user_roles */
  getSelectedCountForRole(role: any): number {
    if (!role?.parent_user_roles) return 0;
    return role.parent_user_roles.filter((e: any) =>
      this.selectedClubs[e.parent_user_role_id],
    ).length;
  }

  /** Selected count for Period filter (season + timeline) */
  getSelectedPeriodCount(): number {
    let count = 0;
    if (this.selectedSeasonId) count++;
    if (this.selectedTimelineId) count++;
    return count;
  }

  /** Selected count for Rank filter */
  getSelectedRankCount(): number {
    return this.selectedRank ? 1 : 0;
  }

  /** Selected count for Event filter */
  getSelectedEventCount(): number {
    return Object.keys(this.selectedEvents).filter((id) =>
      this.selectedEvents[Number(id)],
    ).length;
  }

  getActiveRole(): any {
    if (!this.activeRoleId || !this.parent_role_list) return null;
    return this.parent_role_list.find((r: any) => r.parent_role_id === this.activeRoleId) || null;
  }

  private initializeSelectedFromParents() {
    this.selectedClubs = {};
    if (!this.parent_role_list) return;
    this.parent_role_list.forEach((role: any) => {
      (role.parent_user_roles || []).forEach((item: any) => {
        const id = item.parent_user_role_id;
        this.selectedClubs[id] = this.parents?.includes(id) ?? false;
      });
    });
    this.cdr.detectChanges();
  }

  setActiveRoleTab(parentRoleId: number) {
    this.activeTab = 'club';
    this.activeRoleId = parentRoleId;
    this.searchValue = '';
    this.resetClubsList();
    this.fetchClubsPage(1, false);
    this.cdr.detectChanges();
  }

  onRoleEntitySelect(parentUserRoleId: number) {
    this.selectedClubs[parentUserRoleId] = !this.selectedClubs[parentUserRoleId];
    this.cdr.detectChanges();
  }

  /** Ensures season id is string for dropdown value matching (API may return number) */
  getSeasonIdValue(id: string | number): string {
    return id == null ? '' : String(id);
  }

  trackById(index: number, item: unknown): string | number {
    if (item != null && typeof item === 'object' && 'id' in item) {
      const id = (item as { id?: string | number }).id;
      if (id != null) return id;
    }
    return index;
  }
}